/**
 * Configuración de IA y preferencias de usuario.
 * Se persiste en la tabla `settings` de IndexedDB (clave-valor).
 */

import type { HighlightColor } from '@/types/domain'

/** Identificador de proveedor de IA soportado. */
export type ProviderId = 'openai' | 'anthropic' | 'ollama'

/** Densidad de la rejilla de la biblioteca. */
export type LibraryDensity = 'compact' | 'comfortable' | 'list'

/**
 * Estado de los filtros de la biblioteca, persistido para no perderse al
 * recargar o navegar entre vistas. Se restaura en `library.load()`.
 */
export interface LibraryFilters {
  /** Texto de búsqueda (title/author/tags). */
  search: string
  /** Filtro por estado de lectura; 'all' = sin filtro. */
  statusFilter: 'all' | 'unread' | 'reading' | 'finished'
  /** Criterio de ordenación. */
  sortKey: 'recent' | 'title' | 'author' | 'progress' | 'year' | 'rating' | 'lastRead'
  /** Etiqueta activa (null = todas). */
  tagFilter: string | null
  /** Colección activa (null = todas). */
  collectionFilter: string | null
  /** Estantería activa (null = todas). */
  shelfFilter: string | null
  /** Densidad de la rejilla. */
  density: LibraryDensity
}

/** Cómo se enrutan las llamadas a IA al proveedor. */
export type Airouting = 'direct' | 'proxy'

/**
 * Proveedor de la voz neural del modo audiolibro.
 *
 * Es un ajuste APARTE de `activeProvider` y no un `ProviderId` porque
 * ElevenLabs solo hace TTS: no da chat ni embeddings, así que no tiene nada
 * que hacer en el selector de proveedor ni en `providers`.
 */
export type TtsProvider = 'openai' | 'elevenlabs'

/** Configuración de un proveedor concreto. */
export interface ProviderConfig {
  /** Si el proveedor está habilitado en la UI. */
  enabled: boolean
  /** API key (vacío si se usa proxy o proveedor local sin auth). */
  apiKey: string
  /** Modelo por defecto para este proveedor. */
  model: string
  /**
   * Modelo de embeddings (distinto al de chat). Opcional: si no se define,
   * se usa un default razonable por proveedor (text-embedding-3-small para
   * OpenAI, nomic-embed-text para Ollama).
   */
  embeddingsModel: string
  /** Endpoint base; útil para Ollama o self-hosted. */
  baseUrl: string
}

export interface AiSettings {
  /** Proveedor activo. */
  activeProvider: ProviderId
  routing: Airouting
  /** Endpoint del proxy serverless cuando routing === 'proxy'. */
  proxyEndpoint: string
  /**
   * Token compartido para autenticar contra el Worker proxy. Si el Worker
   * tiene `APP_TOKEN` configurado, se envía en la cabecera `x-app-token`
   * en /generate, /download y /proxy. Vacío = sin auth (dev).
   */
  proxyToken: string
  /** Quién pone la voz neural del audiolibro. Ver `TtsProvider`. */
  ttsProvider: TtsProvider
  /**
   * API key de ElevenLabs (solo TTS). Vive aquí y no en `audio` para heredar
   * lo que ya vale para las demás claves: se cifra y sincroniza con la cuenta
   * y se borra del dispositivo al cerrar sesión (`clearLocalAi`).
   *
   * Va SIEMPRE directa desde el navegador: el Worker proxy solo enruta
   * OpenAI, así que `routing: 'proxy'` no la cubre.
   */
  elevenLabsKey: string
  providers: Record<ProviderId, ProviderConfig>
  /**
   * Temperatura para resúmenes. Más baja = más conservador/fiel.
   */
  temperature: number
  /** Idioma en el que se piden los resúmenes (BCP-47, ej. "es"). */
  summaryLanguage: string
  /**
   * Timestamp ISO de la última versión de la config IA sincronizada con la
   * nube. Se persiste en IndexedDB (dentro de `ai`) para que, al reabrir la
   * app, el pull sepa si la nube es realmente más nueva y sólo entonces
   * pise lo local. `null` = aún no se ha sincronizado nunca.
   */
  lastSyncedAt: string | null
}

/** Tema visual del lector. */
export type Theme = 'light' | 'dark' | 'sepia'

/** Preferencias de tipografía del contenido del libro. */
export interface Typography {
  /** Familia CSS (ej. "Georgia, serif"). */
  fontFamily: string
  /** Tamaño de fuente base en px. */
  fontSize: number
  /** Interlineado (1.0–2.0). */
  lineHeight: number
  /** Margen lateral en porcentaje (0–30). */
  margin: number
  /** Espaciado entre letras en px (0 = default del navegador). Accesibilidad. */
  letterSpacing: number
  /** Espaciado entre palabras en px (0 = default). Accesibilidad. */
  wordSpacing: number
  /** Espaciado extra entre párrafos en em (0 = sin extra). Accesibilidad. */
  paragraphSpacing: number
}

/**
 * Preferencias de accesibilidad del lector (Área G).
 * Toggles que modifican la presentación para lectores con dislexia,
 * baja visión o preferencias de estudio.
 */
export interface Accessibility {
  /** Fuerza la fuente OpenDyslexic (suma a fontFamily si el usuario la pide). */
  dyslexicFont: boolean
  /** Modo de lectura biónica: negrita en el prefijo de cada palabra. */
  bionicReading: boolean
  /** Resaltado karaoke palabra-a-palabra durante el TTS. */
  ttsHighlight: boolean
  /**
   * Mantiene la pantalla encendida mientras se lee (Screen Wake Lock).
   * Evita que el protector de pantalla/bloqueo corte la lectura silenciosa;
   * el TTS ya tiene su propio wake lock en `useNowPlaying`. Default true:
   * el caso habitual al abrir un libro es querer la pantalla activa. Si el
   * usuario prefiere ahorrar batería, lo desactiva en Ajustes.
   */
  keepScreenAwake: boolean
}

export interface SyncSettings {
  /** Si la sincronización con Supabase está activada. */
  enabled: boolean
  /** URL del proyecto Supabase (ej. https://xxxx.supabase.co). */
  supabaseUrl: string
  /** Anon key pública del proyecto Supabase. */
  supabaseAnonKey: string
  /** ISO timestamp de la última sincronización exitosa. */
  lastSyncAt: string | null
  /**
   * Si true, al importar un EPUB localmente se sube automáticamente a la
   * nube en background (fire-and-forget). Solo aplica cuando sync está
   * activo y hay sesión. Default false (respeta el comportamiento
   * local-first; el usuario sube manualmente si quiere).
   */
  autoUploadOnImport: boolean
}

export interface Settings {
  theme: Theme
  typography: Typography
  /** Modo de la app (claro/oscuro) — puede diferir del tema del lector. */
  appMode: 'light' | 'dark' | 'system'
  /** Idioma de la interfaz (no del contenido ni de la salida de IA). */
  locale: AppLocale
  /**
   * Modo de paginación del lector: 'paginated' (páginas) o 'scrolled'
   * (scroll continuo). Se persiste para respetar la preferencia entre
   * sesiones.
   */
  readerFlow: 'paginated' | 'scrolled'
  /**
   * Paleta de colores de subrayados personalizable. Las claves son los
   * identificadores de color del dominio; los valores, hex. Si un
   * highlight usa un color no presente, foliate cae al hex literal.
   */
  highlightColors: Record<HighlightColor, string>
  ai: AiSettings
  sync: SyncSettings
  /** Metas de lectura (motivacionales). */
  readingGoals: ReadingGoals
  /** Filtros de la biblioteca persistidos entre sesiones. */
  libraryFilters: LibraryFilters
  /** Preferencias de accesibilidad del lector (Área G). */
  accessibility: Accessibility
  /** Configuración del Pomodoro (Área H). */
  pomodoro: PomodoroConfig
  /** Modo audiolibro. */
  audio: AudioSettings
  /**
   * True si el usuario cerró la tarjeta de onboarding en biblioteca vacía.
   * Mientras sea false y no haya libros, LibraryView muestra la guía de
   * bienvenida con CTAs (importar, cargar demo, configurar sync/IA).
   */
  onboardingDismissed: boolean
}

/** Idiomas soportados por la interfaz. */
export type AppLocale = 'es' | 'en' | 'pt'

/** Configuración de metas de lectura. */
export interface ReadingGoals {
  /** Objetivo diario de lectura en minutos (0 = sin meta). */
  dailyGoalMinutes: number
}

/**
 * Configuración del temporizador Pomodoro (Área H, #28).
 * Independiente del Modo Zen: solo gestiona foco/descanso y registra el
 * tiempo de foco como sesión de lectura.
 */
export interface PomodoroConfig {
  /** Si el Pomodoro está habilitado (muestra la barra). */
  enabled: boolean
  /** Minutos de foco por ciclo. */
  focusMinutes: number
  /** Minutos de descanso corto. */
  breakMinutes: number
  /** Minutos de descanso largo (tras N ciclos). */
  longBreakMinutes: number
  /** Ciclos de foco antes de un descanso largo. */
  cyclesBeforeLongBreak: number
}

/**
 * Configuración del modo audiolibro.
 *
 * `cacheLimitMb` es visible y ajustable a propósito: el audio pesa ~1 MB por
 * minuto, así que un libro de 10 h son ~600 MB y el tope se nota.
 */
export interface AudioSettings {
  /** Motor preferido al iniciar la escucha. */
  engine: 'browser' | 'neural'
  /** Voz de la API de OpenAI para el motor neural. */
  neuralVoice: string
  /**
   * Voz de ElevenLabs (`voice_id`) cuando `ai.ttsProvider` es 'elevenlabs'.
   * Es una preferencia de escucha, así que vive con el resto del audio; la
   * key, en cambio, va en `ai` (ver `AiSettings.elevenLabsKey`).
   */
  elevenLabsVoice: string
  /** Tope del caché de audio en MB (el LRU desaloja al superarlo). */
  cacheLimitMb: number
}

/**
 * Configuración IA por defecto (sin claves). Se exporta aparte para poder
 * resetear la config IA local al cerrar sesión (`clearLocalAi`): así no
 * quedan API keys ni datos del worker en claro en el dispositivo, mientras
 * la nube conserva la copia cifrada ligada a la cuenta.
 */
export const DEFAULT_AI_SETTINGS: AiSettings = {
  activeProvider: 'openai',
  routing: 'direct',
  proxyEndpoint: '',
  proxyToken: '',
  ttsProvider: 'openai',
  elevenLabsKey: '',
  temperature: 0.4,
  summaryLanguage: 'es',
  lastSyncedAt: null,
  providers: {
    openai: {
      enabled: true,
      apiKey: '',
      model: 'gpt-4o-mini',
      embeddingsModel: 'text-embedding-3-small',
      baseUrl: 'https://api.openai.com/v1',
    },
    anthropic: {
      enabled: true,
      apiKey: '',
      model: 'claude-3-5-sonnet-20241022',
      embeddingsModel: '',
      baseUrl: 'https://api.anthropic.com/v1',
    },
    ollama: {
      enabled: true,
      apiKey: '',
      model: 'llama3.1',
      embeddingsModel: 'nomic-embed-text',
      baseUrl: 'http://localhost:11434',
    },
  },
}

/** Ajustes por defecto. Sirven como base para el primer arranque. */
export const DEFAULT_SETTINGS: Settings = {
  theme: 'dark',
  typography: {
    fontFamily: 'Georgia, serif',
    fontSize: 18,
    lineHeight: 1.6,
    margin: 10,
    letterSpacing: 0,
    wordSpacing: 0,
    paragraphSpacing: 0,
  },
  appMode: 'system',
  locale: 'es',
  readerFlow: 'paginated',
  highlightColors: {
    yellow: '#fde68a',
    green: '#bbf7d0',
    blue: '#bfdbfe',
    pink: '#fbcfe8',
    orange: '#fed7aa',
  },
  ai: structuredClone(DEFAULT_AI_SETTINGS),
  sync: {
    // La sincronización está SIEMPRE habilitada cuando hay credenciales
    // (VITE_SUPABASE_* en .env). No es un ajuste del usuario: el único
    // "interruptor" real es iniciar/cerrar sesión. Si no hay credenciales,
    // el flag queda false y todo opera en modo local (offline-first).
    enabled: true,
    // Provenientes únicamente del entorno (.env.local). No se editan desde
    // la UI. Importante: publishable key (sb_publishable_…), nunca la
    // secret — esta última no debe ir nunca al navegador.
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? '',
    supabaseAnonKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '',
    lastSyncAt: null,
    autoUploadOnImport: false,
  },
  readingGoals: {
    // 20 min/día es un objetivo razonable por defecto ( hábito sostenible).
    dailyGoalMinutes: 20,
  },
  libraryFilters: {
    search: '',
    statusFilter: 'all',
    sortKey: 'recent',
    tagFilter: null,
    collectionFilter: null,
    shelfFilter: null,
    density: 'comfortable',
  },
  accessibility: {
    dyslexicFont: false,
    bionicReading: false,
    ttsHighlight: true,
    keepScreenAwake: true,
  },
  pomodoro: {
    enabled: false,
    focusMinutes: 25,
    breakMinutes: 5,
    longBreakMinutes: 15,
    cyclesBeforeLongBreak: 4,
  },
  audio: {
    engine: 'browser',
    neuralVoice: 'nova',
    // Voz por defecto de la biblioteca de ElevenLabs (la de sus propios
    // ejemplos, presente en cualquier cuenta). Solo es un punto de partida:
    // el selector del reproductor lista las voces reales de la cuenta.
    elevenLabsVoice: 'JBFqnCBsd6RMkjVDRZzb',
    cacheLimitMb: 500,
  },
  onboardingDismissed: false,
}
